import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Bus, BusFront, Loader2, AlertTriangle, UserPlus, X, IdCard, Receipt, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { PhotoCapture, QueueIndicator } from '@/components/construction/PhotoCapture';
import { AddWorkerSheet } from '@/components/construction/AddWorkerSheet';
import { AddExpenseSheet } from '@/components/construction/AddExpenseSheet';
import { AddPerformancePhotoSheet } from '@/components/construction/AddPerformancePhotoSheet';
import { AuthImg } from '@/components/construction/AuthImg';
import { drainQueue, queueCount } from '@/lib/construction-offline';
import { getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface Worker {
  id: string;
  code: string;
  fullName: string;
  skill: string;
  hourlyRate: number | string;
  iqamaNumber: string | null;
  photoUrl: string | null;
}

interface AttendanceDay {
  id: string;
  attendanceDate: string;
  busInAt: string | null;
  busInPhotoUrl: string | null;
  busOutAt: string | null;
  busOutPhotoUrl: string | null;
  entries: { workerId: string; photoUrl: string | null; photoTakenAt: string | null }[];
}

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtTime = (s: string | null) => s ? new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

export default function AttendanceFlowPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [date] = useState(todayStr());
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [day, setDay] = useState<AttendanceDay | null>(null);
  const [loading, setLoading] = useState(true);
  const [queued, setQueued] = useState(0);
  const [draining, setDraining] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [perfOpen, setPerfOpen] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    const [w, d, q] = await Promise.all([
      apiClient.get<Worker[]>(`/api/v1/construction/projects/${projectId}/workers`),
      apiClient.get<AttendanceDay>(`/api/v1/construction/projects/${projectId}/attendance/${date}`),
      queueCount(),
    ]);
    setWorkers(w.data);
    setDay(d.data);
    setQueued(q);
    setLoading(false);
  }, [projectId, date]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-drain queue when network comes back
  useEffect(() => {
    const onOnline = async () => {
      const jwt = getStoredToken();
      if (!jwt) return;
      setDraining(true);
      await drainQueue(API_BASE, jwt);
      setDraining(false);
      refresh();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refresh]);

  async function manualDrain() {
    const jwt = getStoredToken();
    if (!jwt) return;
    setDraining(true);
    await drainQueue(API_BASE, jwt);
    setDraining(false);
    refresh();
  }

  async function handleRemove(w: Worker) {
    if (!projectId) return;
    if (!window.confirm(`Remove ${w.fullName} from attendance?\n\nThis stops them appearing in today's check-in. Their past attendance records are kept.`)) return;
    setRemovingId(w.id);
    try {
      await apiClient.patch(`/api/v1/construction/projects/${projectId}/workers/${w.id}`, {
        isActive: false,
        leftDate: date,
      });
      await refresh();
    } catch (err) {
      window.alert(`Could not remove worker: ${extractErrorMessage(err)}`);
    } finally {
      setRemovingId(null);
    }
  }

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  const busInDone = !!(day?.busInPhotoUrl || day?.busInAt);
  const busOutDone = !!(day?.busOutPhotoUrl || day?.busOutAt);
  const checkedInIds = new Set(day?.entries?.filter(e => e.photoUrl).map(e => e.workerId) ?? []);
  const photographedCount = checkedInIds.size;
  const totalActive = workers.length;
  const allCheckedIn = photographedCount >= totalActive;
  const missingIqamaCount = workers.filter(w => !w.iqamaNumber).length;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard/construction">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </Link>
          <div className="text-sm font-medium">{fmtDate(date)}</div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-3">
        {queued > 0 && (
          <QueueIndicator count={queued} onDrain={manualDrain} busy={draining} />
        )}

        {/* Bus arrival */}
        <div className={`border rounded-lg p-4 flex items-center gap-3 ${busInDone ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' : 'border-border'}`}>
          <div className="h-12 w-12 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
            <Bus className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">Bus arrival</div>
            <div className="text-xs text-muted-foreground font-mono">
              {busInDone ? `Logged at ${fmtTime(day!.busInAt)}` : 'Take photo when bus arrives'}
            </div>
          </div>
          <PhotoCapture
            endpoint={`/projects/${projectId}/bus`}
            extraFields={{ direction: 'in', date }}
            label="Photo"
            done={busInDone}
            onCaptured={() => { refresh(); queueCount().then(setQueued); }}
          />
        </div>

        {/* Worker check-ins */}
        <div className="space-y-2">
          <div className="flex items-center justify-between mt-4 mb-1">
            <div className="text-sm font-medium">Worker check-in</div>
            <div className="text-xs text-muted-foreground">{photographedCount}/{totalActive}</div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2 border-dashed"
            onClick={() => setAddOpen(true)}
          >
            <UserPlus className="h-4 w-4" />
            Add worker
          </Button>

          {workers.map((w) => {
            const isDone = checkedInIds.has(w.id);
            const isRemoving = removingId === w.id;
            return (
              <div key={w.id} className="border border-border rounded-lg p-3 flex items-center gap-3">
                {w.photoUrl ? (
                  <AuthImg
                    src={w.photoUrl}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover bg-muted flex-shrink-0"
                    fallback={
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium text-muted-foreground">
                        {w.fullName.split(' ').map(p => p[0]).slice(0,2).join('')}
                      </div>
                    }
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium text-muted-foreground">
                    {w.fullName.split(' ').map(p => p[0]).slice(0,2).join('')}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{w.fullName}</div>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                    <span>{w.skill} · {Number(w.hourlyRate)} SAR/hr</span>
                    {!w.iqamaNumber && (
                      <span className="inline-flex items-center text-amber-700 dark:text-amber-400" title="Iqama not on file">
                        · <IdCard className="h-3 w-3 ml-0.5" />
                      </span>
                    )}
                  </div>
                </div>
                <PhotoCapture
                  endpoint={`/projects/${projectId}/worker-checkin`}
                  extraFields={{ workerId: w.id, date }}
                  label="Photo"
                  done={isDone}
                  iconOnly
                  onCaptured={() => { refresh(); queueCount().then(setQueued); }}
                />
                <button
                  type="button"
                  aria-label={`Remove ${w.fullName}`}
                  disabled={isRemoving || isDone}
                  onClick={() => handleRemove(w)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title={isDone ? 'Cannot remove a worker who has checked in today' : 'Remove worker'}
                >
                  {isRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                </button>
              </div>
            );
          })}

          {missingIqamaCount > 0 && (
            <div className="mt-2 flex items-center gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-xs px-3 py-2">
              <IdCard className="h-4 w-4 flex-shrink-0" />
              <span>{missingIqamaCount} worker{missingIqamaCount > 1 ? 's' : ''} missing iqama — add from the Workers tab when issued.</span>
            </div>
          )}
        </div>

        {/* Bus departure */}
        <div className={`border rounded-lg p-4 flex items-center gap-3 mt-4 ${busOutDone ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' : 'border-border'}`}>
          <div className="h-12 w-12 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
            <BusFront className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">Bus departure</div>
            <div className="text-xs text-muted-foreground font-mono">
              {busOutDone ? `Logged at ${fmtTime(day!.busOutAt)}` : 'Take photo when bus leaves'}
            </div>
          </div>
          <PhotoCapture
            endpoint={`/projects/${projectId}/bus`}
            extraFields={{ direction: 'out', date }}
            label="Photo"
            done={busOutDone}
            disabled={!busInDone}
            onCaptured={() => { refresh(); queueCount().then(setQueued); }}
          />
        </div>

        {/* Day summary */}
        {busInDone && busOutDone && (
          <div className="mt-4 p-3 rounded-md bg-muted text-sm space-y-1">
            <div>Site time: <span className="font-medium">
              {(() => {
                if (!day?.busInAt || !day?.busOutAt) return '—';
                const hrs = (new Date(day.busOutAt).getTime() - new Date(day.busInAt).getTime()) / 3600000;
                return `${hrs.toFixed(1)} hrs`;
              })()}
            </span></div>
            {!allCheckedIn && (
              <div className="flex items-center gap-1 text-amber-700 dark:text-amber-400 text-xs">
                <AlertTriangle className="h-3 w-3" />
                {totalActive - photographedCount} worker{totalActive - photographedCount > 1 ? 's' : ''} without photo — flagged for admin
              </div>
            )}
          </div>
        )}

        {/* PR 22: add work photo */}
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2 mt-4 border-dashed"
          onClick={() => setPerfOpen(true)}
        >
          <ImageIcon className="h-4 w-4" />
          Add work photo
        </Button>

        {/* PR 21: log expense */}
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2 mt-2 border-dashed"
          onClick={() => setExpenseOpen(true)}
        >
          <Receipt className="h-4 w-4" />
          Log expense
        </Button>
      </main>

      {projectId && (
        <AddWorkerSheet
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onCreated={() => { refresh(); }}
          projectId={projectId}
        />
      )}

      {projectId && (
        <AddExpenseSheet
          open={expenseOpen}
          onClose={() => setExpenseOpen(false)}
          onCreated={() => { refresh(); }}
          projectId={projectId}
        />
      )}

      {projectId && (
        <AddPerformancePhotoSheet
          open={perfOpen}
          onClose={() => setPerfOpen(false)}
          onCreated={() => { refresh(); }}
          projectId={projectId}
        />
      )}
    </div>
  );
}
